import { useContext } from 'react';

import { getGlobalObject } from '../getGlobalObject.js';
import { RumComponentContext } from './rumComponentContext.js';

/**
 * A hook that allows tracking of errors in Real User Monitoring (RUM).
 * It automatically adds the component chain/breadcrumbs from <RumComponentContextProvider>.
 *
 * @function
 *
 * @returns - A function that can be used to track a RUM error. If the Datadog RUM SDK is not initialized, it returns a no-op function.
 *
 * @throws - If the Datadog RUM SDK is not initialized, a warning is logged to the console.
 */
export const useRumError = () => {
  const componentContext = useContext(RumComponentContext);
  // @ts-ignore DD_RUM is defined in the SDK
  const RumGlobal = getGlobalObject<Window>().DD_RUM;

  if (!RumGlobal) {
    console.warn(
      '@datadog/rum-react-integration: Datadog RUM SDK is not initialized.',
    );
    return () => {};
  }

  return (
    error: unknown,
    customAttributes: object | undefined,
    source: 'custom' | 'network' | 'source' | undefined,
  ) => {
    RumGlobal.addError(
      error,
      {
        ...componentContext.customAttributes,
        ...customAttributes,
        react: {
          breadcrumbs: componentContext.componentBreadCrumbs,
          ...(customAttributes as any)?.react,
        },
      },
      source,
    );
  };
};
